import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateChannel } from '@/api/fastchannel';
import { trackFeatureAction } from '@/lib/analytics-events';
import { PlayoutModeField } from './PlayoutModeField';
import { DEFAULT_PLAYOUT_MODE, playoutModeToInput, type PlayoutMode } from './playout-modes';

interface CreateChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceUuid: string;
  /** Tenant carried over from existing channels (FastChannel is single-tenant per service). */
  tenant?: string;
  onCreated: (channelId: string) => void;
}

const KEY_RE = /^[a-z0-9][a-z0-9-]*$/;

export function CreateChannelDialog({
  open,
  onOpenChange,
  serviceUuid,
  onCreated,
}: CreateChannelDialogProps) {
  const { show } = useToast();
  const [title, setTitle] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<PlayoutMode>(DEFAULT_PLAYOUT_MODE);

  const reset = () => {
    setTitle('');
    setName('');
    setDescription('');
    setMode(DEFAULT_PLAYOUT_MODE);
  };

  const create = useCreateChannel(serviceUuid, {
    onSuccess: (data) => {
      show({ tone: 'success', title: 'Channel created' });
      trackFeatureAction('fastchannel', 'channel', 'create', { id: data?.id });
      onOpenChange(false);
      const id = data?.id;
      reset();
      if (id) onCreated(id);
    },
    onError: () => show({ tone: 'danger', title: 'Could not create channel' }),
  });

  const keyValid = KEY_RE.test(name.trim());
  const valid = title.trim().length > 0 && keyValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add channel</DialogTitle>
          <DialogDescription>
            A channel is an activated FAST playout stream. The key becomes part of its stream URL.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Title" required description="Display name for the channel.">
              {(props) => (
                <Input
                  {...props}
                  value={title}
                  placeholder="VTV Giải trí"
                  onChange={(e) => setTitle(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Key"
              required
              description="Lowercase letters, digits and hyphens. Used in the stream URL."
              error={name.length > 0 && !keyValid ? 'Use lowercase letters, digits and hyphens.' : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  value={name}
                  placeholder="vtv-giai-tri"
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Field>
            <Field label="Description" description="Optional notes about this channel.">
              {(props) => (
                <Textarea
                  {...props}
                  value={description}
                  rows={2}
                  placeholder="Entertainment channel"
                  onChange={(e) => setDescription(e.target.value)}
                />
              )}
            </Field>
            <PlayoutModeField value={mode} onChange={setMode} />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid || create.isPending}
            onClick={() =>
              create.mutate({
                title: title.trim(),
                name: name.trim(),
                description: description.trim() || undefined,
                ...playoutModeToInput(mode),
                status: 'active',
              })
            }
          >
            {create.isPending ? 'Creating…' : 'Create channel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
